import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import { SCORECARD, SCORECARD_VOCAB } from "@shared/scorecard";
import { Body } from "../../../primitives/data-display/Body";
import { Note } from "../../../primitives/data-display/Note";
import { Meta } from "../../../primitives/data-display/Meta";
import { Panel } from "../../../primitives/surfaces/Panel";
import { gaps } from "../../../primitives/spacing";
import OverrideMode from "./OverrideMode";
import type { DimensionPreAnnotation } from "../../../../lib/corpusApi";
import {
  FieldPanelHeader,
  FieldPanelActions,
  HelpPanel,
  PanelContainedButton,
  PanelRoot,
  ChipRow,
  ItalicNote,
  type FieldPanelBase,
} from "./panel_shared";

export interface FieldReviewPanelProps extends FieldPanelBase {
  preAnnotation: DimensionPreAnnotation;
  onApprove: () => void;
}

const DesktopHint = styled(Stack)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: { display: "flex" },
}));

const EvidenceBody = styled(Body)({ fontStyle: "italic" });

/**
 * The single active field under review: extracted value + confidence and the algorithm versions
 * behind it, evidence quote or the model's justification, and the three backlog actions (approve / override / mark empty) — each commits and advances to the
 * next work item immediately, so there is no separate save step.
 */
export function FieldReviewPanel({
  field,
  preAnnotation,
  override,
  saving,
  helpOpen,
  onApprove,
  onOverride,
  onMarkEmpty,
  onReject,
  onHelpToggle,
}: FieldReviewPanelProps) {
  const vocab = SCORECARD_VOCAB[field] ?? [];
  const config = SCORECARD[field];
  const labelDescriptions = config?.labelDescriptions;
  const hasSuggestion = preAnnotation.labels.length > 0;
  const confidencePct =
    preAnnotation.confidence !== null ? Math.round(preAnnotation.confidence * 100) : null;

  return (
    <PanelRoot gap={gaps.base}>
      <FieldPanelHeader field={field} helpOpen={helpOpen} onHelpToggle={onHelpToggle} />

      <div style={{ display: helpOpen ? undefined : "none" }}>
        <HelpPanel
          description={config?.description}
          vocab={vocab}
          labelDescriptions={labelDescriptions}
        />
      </div>

      {override.mode ? (
        <OverrideMode
          labels={vocab}
          labelDescriptions={labelDescriptions}
          selected={override.selection}
          multiple={!config?.singleSelect}
          focusIndex={override.focusIndex}
          onFocusChange={override.onFocusChange}
          onChange={override.onSelectionChange}
          onConfirm={override.onConfirm}
          onCancel={override.onCancel}
        />
      ) : (
        <>
          <Panel tone="muted" pad="sm">
            <Stack gap={gaps.tight}>
              <Meta>
                {confidencePct !== null
                  ? `Confidence ${confidencePct}% — Document service extracted:`
                  : "Document service extracted:"}
              </Meta>
              {hasSuggestion ? (
                <ChipRow direction="row" gap={gaps.tight}>
                  {preAnnotation.labels.map((label) => (
                    <Chip key={label} label={label} size="small" />
                  ))}
                </ChipRow>
              ) : (
                <ItalicNote>Nothing extracted — your call</ItalicNote>
              )}
              {preAnnotation.algorithm_version && (
                <Meta>Algorithm {preAnnotation.algorithm_version.split(",").join(" → ")}</Meta>
              )}
            </Stack>
          </Panel>

          {preAnnotation.quote && (
            <Stack gap={gaps.tight}>
              <Meta>Evidence</Meta>
              <Panel tone="accent" pad="sm">
                <EvidenceBody>&ldquo;{preAnnotation.quote}&rdquo;</EvidenceBody>
              </Panel>
            </Stack>
          )}

          {preAnnotation.justification && (
            <Stack gap={gaps.tight}>
              <Meta>Justification</Meta>
              <Panel tone="accent" pad="sm">
                <Body>{preAnnotation.justification}</Body>
              </Panel>
            </Stack>
          )}

          <Divider />

          <FieldPanelActions
            primary={
              <PanelContainedButton
                variant="contained"
                size="small"
                onClick={onApprove}
                disabled={!hasSuggestion || saving}
              >
                ↵ Approve
              </PanelContainedButton>
            }
            saving={saving}
            onOverride={onOverride}
            onMarkEmpty={onMarkEmpty}
            onReject={onReject}
          />

          {/* Keyboard shortcut hint — desktop only */}
          <DesktopHint>
            <Note>
              ↵ approve · O override · E mark empty · ? help · each action saves and advances
            </Note>
          </DesktopHint>
        </>
      )}
    </PanelRoot>
  );
}
